import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Title, Text, Card, Button } from '@tremor/react'
import { DocumentTextIcon, PlusIcon, TrashIcon, PencilIcon, SparklesIcon } from '@heroicons/react/24/outline'
import { getResearchQuestions, createResearchQuestion, updateResearchQuestion, deleteResearchQuestion, generateAISuggestions } from '../../Requests/ResearchQuestionRequests'
import { sanitizeUserInput } from '../../utils/security'
import { useMyContext } from '../ContextProviders/CreditsContextProvider'
import UpgradedLoader from '../UpgradedLoader'
import ResearchQuestionFormModal from './ResearchQuestionFormModal'
import CreateManualQuestionModal from './CreateManualQuestionModal'
import ConfirmationModal from '../ConfirmationModal'

function QuestaoPartidaTab({ projectUuid, onNavigateNext }) {
    const { t, i18n } = useTranslation()
    const { setCredits } = useMyContext()
    const [researchQuestions, setResearchQuestions] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [showFormModal, setShowFormModal] = useState(false)
    const [showManualModal, setShowManualModal] = useState(false)
    const [editingQuestion, setEditingQuestion] = useState(null)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [questionToDelete, setQuestionToDelete] = useState(null)

    const loadResearchQuestions = useCallback(async () => {
        try {
            setLoading(true)
            setError(null)
            const data = await getResearchQuestions(projectUuid)
            setResearchQuestions(data.data || [])
        } catch (err) {
            console.error('Failed to load research questions:', err)
            setError(t('Research.QuestaoPartida.LoadError'))
        } finally {
            setLoading(false)
        }
    }, [projectUuid, t])

    useEffect(() => {
        loadResearchQuestions()
    }, [loadResearchQuestions])

    const handleCreateManual = async (questionData) => {
        try {
            const cleanData = {
                question: sanitizeUserInput(questionData.question),
                status: questionData.status || 'draft'
            }
            const response = await createResearchQuestion(projectUuid, cleanData)
            setResearchQuestions(prev => [response.data, ...prev])
            setShowManualModal(false)
            
            // Navigate to research goals after creating a research question
            if (onNavigateNext) {
                setTimeout(() => onNavigateNext(), 500) // Small delay for UX
            }
        } catch (err) {
            console.error('Failed to create research question:', err)
            throw new Error(err.error || t('Research.QuestaoPartida.CreateError'))
        }
    }

    const handleUpdate = async (questionUuid, questionData) => {
        try {
            const cleanData = {
                question: sanitizeUserInput(questionData.question),
                status: questionData.status
            }
            const response = await updateResearchQuestion(projectUuid, questionUuid, cleanData)
            setResearchQuestions(prev => 
                prev.map(q => q.uuid === questionUuid ? response.data : q)
            )
            setEditingQuestion(null)
        } catch (err) {
            console.error('Failed to update research question:', err)
            throw new Error(err.error || t('Research.QuestaoPartida.UpdateError'))
        }
    }

    const handleDeleteClick = (questionUuid) => {
        setQuestionToDelete(questionUuid)
        setShowDeleteModal(true)
    }

    const handleDeleteConfirm = async () => {
        if (!questionToDelete) return

        try {
            await deleteResearchQuestion(projectUuid, questionToDelete)
            setResearchQuestions(prev => prev.filter(q => q.uuid !== questionToDelete))
            setShowDeleteModal(false)
            setQuestionToDelete(null)
        } catch (err) {
            console.error('Failed to delete research question:', err)
            alert(err.error || t('Research.QuestaoPartida.DeleteError'))
        }
    }

    const handleGenerateAI = async (formResponses) => {
        try {
            const cleanResponses = formResponses.map(response => ({
                question_number: response.question_number,
                question_text: sanitizeUserInput(response.question_text),
                user_response: sanitizeUserInput(response.user_response)
            }))

            const response = await generateAISuggestions(projectUuid, cleanResponses, i18n.language)
            
            // Auto-create research questions from all AI suggestions
            const newQuestions = []
            for (const suggestion of response.data.suggestions) {
                try {
                    const suggestionData = {
                        question: sanitizeUserInput(suggestion.question),
                        reasoning: sanitizeUserInput(suggestion.reasoning),
                        type: 'ai_generated',
                        status: 'draft' // All start as draft, user will activate one
                    }
                    const questionResponse = await createResearchQuestion(projectUuid, suggestionData)
                    newQuestions.push(questionResponse.data)
                } catch (err) {
                    console.error('Failed to create research question from suggestion:', err)
                }
            }
            
            // Add new questions to the list
            setResearchQuestions(prev => [...newQuestions, ...prev])
            
            // Update frontend credit balance
            if (response.data.updated_credits !== undefined) {
                setCredits(response.data.updated_credits)
            }
            
            // Close the form modal
            setShowFormModal(false)
        } catch (err) {
            console.error('Failed to generate AI suggestions:', err)
            throw new Error(err.error || t('Research.QuestaoPartida.GenerateError'))
        }
    }

    const handleSelectQuestion = async (questionUuid) => {
        try {
            // First deactivate all other questions
            const updatePromises = researchQuestions.map(q => {
                if (q.uuid === questionUuid) {
                    return updateResearchQuestion(projectUuid, q.uuid, { status: 'active' })
                } else if (q.status === 'active') {
                    return updateResearchQuestion(projectUuid, q.uuid, { status: 'draft' })
                }
                return Promise.resolve()
            })

            await Promise.all(updatePromises)
            
            // Update local state
            setResearchQuestions(prev => 
                prev.map(q => ({
                    ...q,
                    status: q.uuid === questionUuid ? 'active' : (q.status === 'active' ? 'draft' : q.status)
                }))
            )
            
            // Navigate to research goals after selecting a research question
            if (onNavigateNext) {
                setTimeout(() => onNavigateNext(), 500) // Small delay for UX
            }
        } catch (err) {
            console.error('Failed to activate research question:', err)
            alert(err.error || t('Research.QuestaoPartida.SelectError'))
        }
    }

    const getStatusBadge = (status) => {
        const statusColors = {
            draft: 'bg-gray-100 text-gray-800',
            active: 'bg-iavisus-900 text-white', 
            archived: 'bg-yellow-100 text-yellow-800'
        }
        
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[status] || statusColors.draft}`}>
                {t(`Research.QuestaoPartida.Status.${status}`)}
            </span>
        )
    }

    if (loading) {
        return (
            <div className="flex justify-center items-center py-12">
                <UpgradedLoader className="animate-spin" w={8} h={8} bg="bg-iavisus-100" />
            </div>
        )
    }

    return (
        <div data-testid="questao-partida-container" className="space-y-6">
            <div>
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                        <DocumentTextIcon className="h-6 w-6 text-iavisus-100" />
                        <Title className="text-lg font-semibold">
                            {t('Research.QuestaoPartida.Title')}
                        </Title>
                    </div>
                    <div className="flex space-x-2">
                        <Button
                            data-testid="questao-partida-create-btn"
                            size="sm"
                            icon={PlusIcon}
                            onClick={() => setShowManualModal(true)}
                            className="bg-white border border-iavisus-100 text-iavisus-100 hover:bg-iavisus-50"
                        >
                            {t('Research.QuestaoPartida.CreateManual')}
                        </Button>
                        <Button
                            data-testid="questao-partida-ai-btn"
                            size="sm"
                            icon={SparklesIcon}
                            onClick={() => setShowFormModal(true)}
                            className="bg-iavisus-100 text-white hover:bg-iavisus-200"
                        >
                            {t('Research.QuestaoPartida.GenerateAI')}
                        </Button>
                    </div>
                </div>
                
                <Text className="text-gray-600 mb-6">
                    {t('Research.QuestaoPartida.Description')}
                </Text>

                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                        <Text className="text-red-600">{error}</Text>
                    </div>
                )}

                {researchQuestions.length === 0 ? (
                    <div data-testid="questao-partida-empty" className="bg-gray-50 rounded-lg p-6 text-center">
                        <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <Text className="text-gray-500 mb-4">
                            {t('Research.QuestaoPartida.EmptyState')}
                        </Text>
                        <Text className="text-gray-400 text-sm">
                            {t('Research.QuestaoPartida.EmptyStateHint')}
                        </Text>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {/* All research questions */}
                        {researchQuestions.map((question) => (
                            <Card key={question.uuid} data-testid={`questao-partida-item-${question.uuid}`} className="p-4">
                                <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                        <div className="flex items-center space-x-2 mb-2">
                                            <span className={`inline-block w-2 h-2 rounded-full ${
                                                question.type === 'ai_generated' ? 'bg-iavisus-100' : 'bg-blue-500'
                                            }`}></span>
                                            <Text className="text-xs text-gray-500">
                                                {question.type === 'ai_generated' ? 
                                                    t('Research.QuestaoPartida.AIGenerated') : 
                                                    t('Research.QuestaoPartida.ManualType')
                                                }
                                            </Text>
                                            {getStatusBadge(question.status)}
                                        </div>
                                        <Text className="text-gray-900 mb-2">
                                            {question.question || t('Research.QuestaoPartida.EmptyQuestion')}
                                        </Text>
                                        {question.reasoning && (
                                            <details className="mb-2">
                                                <summary className="text-sm text-gray-600 cursor-pointer hover:text-gray-800">
                                                    {t('Research.QuestaoPartida.ShowReasoning')}
                                                </summary>
                                                <Text className="text-sm text-gray-600 mt-2 pl-4 border-l-2 border-gray-200">
                                                    {question.reasoning}
                                                </Text>
                                            </details>
                                        )}
                                        <div className="flex items-center space-x-4 text-xs text-gray-500">
                                            <span>{t('Research.QuestaoPartida.CreatedBy')} {question.user?.name || t('Common.Unknown')}</span>
                                            <span>{new Date(question.created_at).toLocaleDateString()}</span>
                                            {question.credits_used > 0 && (
                                                <span>{question.credits_used} {t('Common.Credits')}</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex space-x-2 ml-4">
                                        {question.status !== 'active' && (
                                            <button
                                                onClick={() => handleSelectQuestion(question.uuid)}
                                                className="px-3 py-1 text-sm bg-iavisus-100 text-white rounded-md hover:bg-iavisus-200 transition-colors"
                                                title={t('Research.QuestaoPartida.SelectQuestion')}
                                            >
                                                {t('Research.QuestaoPartida.Select')}
                                            </button>
                                        )}
                                        <button
                                            onClick={() => setEditingQuestion(question)}
                                            className="p-1 text-gray-500 hover:text-gray-700"
                                            title={t('Common.Edit')}
                                        >
                                            <PencilIcon className="h-4 w-4" />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteClick(question.uuid)}
                                            className="p-1 text-red-500 hover:text-red-700"
                                            title={t('Common.Delete')}
                                        >
                                            <TrashIcon className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                )}
            </div>

            {/* Modals */}
            {showFormModal && (
                <ResearchQuestionFormModal
                    isOpen={showFormModal}
                    onClose={() => setShowFormModal(false)}
                    onSubmit={handleGenerateAI}
                    language={i18n.language}
                />
            )}

            {showManualModal && (
                <CreateManualQuestionModal
                    isOpen={showManualModal}
                    onClose={() => setShowManualModal(false)}
                    onSubmit={handleCreateManual}
                />
            )}

            {editingQuestion && (
                <CreateManualQuestionModal
                    isOpen={!!editingQuestion}
                    onClose={() => setEditingQuestion(null)}
                    onSubmit={(data) => handleUpdate(editingQuestion.uuid, data)}
                    initialData={editingQuestion}
                    isEditing={true}
                />
            )}

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                open={showDeleteModal}
                setOpen={setShowDeleteModal}
                title={t('Common.Delete')}
                message={t('Research.QuestaoPartida.DeleteConfirm')}
                confirmText={t('Common.Delete')}
                cancelText={t('Common.Cancel')}
                variant="danger"
                onConfirm={handleDeleteConfirm}
            />

        </div>
    )
}

export default QuestaoPartidaTab